import React from 'react';
import ValidateSCU from './ValidateSCU.jsx';
import PropTypes from 'prop-types';
import connectToI18n from 'modules/i18n/connectToI18n';
import { connect } from 'react-redux';
import { userSelector, imperativeEditUser, refreshCurrentUser } from 'modules/currentUser';
import * as R from 'ramda';
import AppChannel from 'modules/channels/App';
import emptyObject from 'modules/utils/emptyObject';
import { INITIAL_LOADING, READY, SAVING } from 'modules/utils/ajaxStatuses';
import { createFarm } from 'api/farms';
import { createLeaderProfile, updateUser } from 'api/users';

import { validate, validators } from 'modules/validation';

export class ValidateSCUContainer extends React.Component {
    static propTypes = {
        user: PropTypes.object.isRequired,
        imperativeEditUser: PropTypes.func.isRequired,
        refreshCurrentUser: PropTypes.func.isRequired,
        match: PropTypes.object.isRequired,
        trans: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = {
            clientErrors: emptyObject,
            serverErrors: emptyObject,
            showErrors: false,
            // We need to use this form for the next method fromModelToForm to be defined on the class
            // Via the class properties babel plugin
            formData: this.fromModelToForm(this.props.user),
            status: INITIAL_LOADING,
        };
    }

    handleOpenFarmSpace = () => {
        createFarm()
            .then(() => {
                this.props.refreshCurrentUser().then(() => {
                    AppChannel.commands.execute('navigate', 'producers/folder', true);
                });
            })
            .catch(() => {});
    };

    handleCreateLeaderProfile = () => {
        this.setState({
            creatingLeaderProfile: true,
        });

        createLeaderProfile(this.props.user.id)
            .then(() => {
                this.props.imperativeEditUser(
                    R.mergeRight({
                        leaderProfile: {
                            blob: '{}',
                            status: 'init',
                        },
                    })
                );
                AppChannel.commands.execute('navigate', 'progress', true);
            })
            .catch(() => {});
    };

    fromFormToModel = formData => {
        return {
            phone: formData.phone,
            address: {
                street: formData.street,
                city: formData.cityId,
            },
            hasAcceptedSCU: formData.hasAcceptedSCU,
        };
    };

    fromModelToForm = user => {
        return {
            phone: user.phone || '',
            street: user.address?.street || '',
            cityId: user.address?.city.id,
            country: user.address?.country,
            zipCode: user.address?.city.zipCode,
            hasAcceptedSCU: false,
        };
    };

    validate = formData => {
        const { assertNotBlank, assertChecked } = validators(this.props.trans);

        return validate.all([
            validate.prop('hasAcceptedSCU', assertChecked),
            validate.prop('phone', assertNotBlank),
            validate.prop('street', assertNotBlank),
            validate.prop('cityId', assertNotBlank),
        ])(formData);
    };

    edit = (field, value) => {
        const formData = { ...this.state.formData };
        formData[field] = value;

        const [, clientErrors] = this.validate(formData);

        this.setState({
            clientErrors,
            formData,
        });
    };

    submit = () => {
        const [isValid, clientErrors] = this.validate(this.state.formData);
        const { user } = this.props;

        if (!isValid) {
            return this.setState({
                clientErrors,
                showErrors: true,
            });
        }

        this.setState({
            status: SAVING,
        });

        const data = this.fromFormToModel(this.state.formData);
        return updateUser(user.id, data)
            .catch(message => {
                if (message?.responseJSON?.detail?.errors) {
                    this.setState({
                        serverErrors: message.responseJSON.detail.errors,
                        showErrors: true,
                        status: READY,
                    });
                }
            })
            .then(() => {
                if (this.props.match.params.type === 'host') {
                    this.handleCreateLeaderProfile();
                } else {
                    this.handleOpenFarmSpace();
                }
            });
    };

    render() {
        return (
            <ValidateSCU
                {...this.state}
                edit={this.edit}
                submit={this.submit}
                type={this.props.match.params.type || ''}
                isAccepted={this.state.formData.hasAcceptedSCU}
            />
        );
    }
}

const mapStateToProps = state => ({
    user: userSelector(state),
});

export default connect(mapStateToProps, { imperativeEditUser, refreshCurrentUser })(
    connectToI18n(ValidateSCUContainer)
);
